import { DeleteIcon, EditIcon, InfiniteTable } from '@/components/_common';
import { useDeleteNfsInstallment } from '@/core/domain/invoice/NfsInstallments/Installment.hook';
import { getTableScrollX } from '@/utils/table';
import { Divider, Modal, Space } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDisclosure } from '@/hooks';
import NfsDeductionModalForm from '@/components/Fiscal/Invoice/NfsDeductions/NfsDeductionModalForm';
import { quantityFormatter } from '@/utils/number';
import { useDeductions } from '@/core/domain/invoice/Deduction/Deduction.hook';
import { Deductions } from '@/core/domain/invoice/Deduction/Deduction.types';
import { applyMask } from '@/utils/mask';
import { cnpjMask, cpfMask } from '@/utils/mask/commonMasks';
import formatterPrice from '@/utils/number/formatterPrice';

interface Props {
  invoiceId: number;
  disabledFields: boolean;
}

const InvoiceDeductionNFSETable: React.FC<Props> = ({
  invoiceId,
  disabledFields,
}) => {
  const { data, isLoading } = useDeductions(invoiceId);

  const [deductionId, setDeductionId] = useState<number>();
  const [modalCreateInstallmentOpened, modalCreateInstallmentAction] =
    useDisclosure();

  const removeMutation = useDeleteNfsInstallment();

  const handleEditDeduction = (deductionId: number) => {
    if (deductionId) {
      setDeductionId(deductionId);

      modalCreateInstallmentAction.open();
    }
  };

  const handleRemoveClick = (deductionId: number) => {
    Modal.confirm({
      title: 'Remoção da dedução',
      content:
        'Deseja realmente remover a dedução, esse processo será irreverssível',
      onOk: async () => {
        await removeMutation.mutateAsync({ invoiceId, id: deductionId });
      },
      okType: 'danger',
    });
  };

  const columns: ColumnsType<Deductions> = [
    {
      title: 'Cpf/Cnpj Referência',
      dataIndex: 'cpfCnpjReference',
      key: 'cpfCnpjReference',
      width: 80,
      ellipsis: true,
      render: (value: string) => {
        if (!value) {
          return '-';
        }
        if (value?.length === 11) {
          return applyMask(value, cpfMask);
        }
        if (value?.length === 14) {
          return applyMask(value, cnpjMask);
        }
        return value;
      },
    },
    {
      title: 'Tipo de dedução',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      ellipsis: true,
      render: (_, row) => {
        if (row.type === 1) {
          return 'Despesas com Materiais';
        } else {
          return 'Despesas com Sub-empreitada';
        }
      },
    },
    {
      title: 'Forma de dedução',
      dataIndex: 'form',
      key: 'form',
      width: 100,
      ellipsis: true,
      render: (_, row) => {
        if (row.form === 1) {
          return 'Percentual';
        } else {
          return 'Valor';
        }
      },
    },
    {
      title: 'Dedução (%)',
      dataIndex: 'deductionPercentage',
      key: 'deductionPercentage',
      width: 100,
      ellipsis: true,
      render: (value) => quantityFormatter(value ?? 0),
    },
    {
      title: 'Dedução (R$)',
      dataIndex: 'deductionValue',
      key: 'deductionValue',
      width: 100,
      ellipsis: true,
      render: (value) => formatterPrice(value || 0),
    },
    {
      title: 'Número da NF de Referência',
      dataIndex: 'referenceNfNumber',
      key: 'referenceNfNumber',
      width: 100,
      ellipsis: true,
    },
    {
      title: 'Valor Total da NF de Referência',
      dataIndex: 'referenceTotalValue',
      key: 'referenceTotalValue',
      width: 100,
      ellipsis: true,
      render: (value) => formatterPrice(value || 0),
    },
    {
      dataIndex: 'actions',
      title: <FormattedMessage id="customers.list.header.actions" />,
      render: (_, record) => (
        <Space>
          <EditIcon
            iconProps={{ onClick: () => handleEditDeduction(record.id) }}
            disabled={disabledFields}
          />

          <Divider type="vertical" />

          <DeleteIcon
            onClick={() => handleRemoveClick(record.id)}
            disabled={disabledFields}
          />
        </Space>
      ),
      ellipsis: true,
      width: 100,
      align: 'right',
      fixed: 'right',
    },
  ];

  return (
    <>
      <InfiniteTable
        columns={columns}
        dataSource={data || []}
        scroll={{ x: getTableScrollX(columns) }}
        rowKey={(row) => row.id}
        pagination={false}
        loading={isLoading}
      />

      <NfsDeductionModalForm
        open={modalCreateInstallmentOpened}
        onCancel={modalCreateInstallmentAction.close}
        onOk={() => null}
        invoiceId={Number(invoiceId)}
        deductionId={deductionId}
      />
    </>
  );
};

export default InvoiceDeductionNFSETable;
